import {
  Dispatch,
  createContext,
  useCallback,
  useContext,
  useReducer,
  ReactNode,
} from "react";
import {
  CreateVacancy,
  UpdateVacancyDto,
  Vacancy,
} from "../../models/vacancy/vacancy";
import { IStateAction } from "../../helpers/state-helpers/state-action";
import { genericCrudReducer } from "../../helpers/state-helpers/generic-crud-reducer";
import {
  AddItemAction,
  SetItemsAction,
  UpdateItemAction,
} from "../../helpers/state-helpers/crud-actions";
import { Teacher } from "../../models/teacher/teacher";
import { SubstituteSchedulerLesson } from "../../components/scheduler/SubstituteScheduler";
import { useVacancyApi } from "../../apis/vacancy-api";
import usePermissions from "../../hooks/usePermissions";
import { PERMISSIONS } from "../../auth/permissions";
import useAuthData from "../../auth/useAuthData";

const VacanciesContext = createContext<Vacancy[]>([]);
const VacaciesDispatchContext = createContext<
  Dispatch<IStateAction<Vacancy>> | undefined
>(undefined);

export function VacanciesProvider({ children }: { children: ReactNode }) {
  const [vacancies, dispatch] = useReducer(genericCrudReducer<Vacancy>, []);

  return (
    <VacanciesContext.Provider value={vacancies}>
      <VacaciesDispatchContext.Provider value={dispatch}>
        {children}
      </VacaciesDispatchContext.Provider>
    </VacanciesContext.Provider>
  );
}

export function useVacanciesState() {
  const vacancies = useContext(VacanciesContext);
  const dispatch = useContext(VacaciesDispatchContext);
  const { createVacancy, getVacancy, updateVacancyWithLessons } =
    useVacancyApi();
  const { hasPermissions } = usePermissions();
  const { currentTeacher } = useAuthData();

  if (!dispatch) {
    throw Error("Dispatch cannot be undefined");
  }

  const getAll = useCallback(
    async (fn: () => Promise<Vacancy[]>) => {
      const vacancies = await fn();
      dispatch(new SetItemsAction(vacancies));
    },
    [dispatch],
  );

  const add = useCallback(
    async (vacancy: CreateVacancy, schooldId: string) => {
      const id = await createVacancy(vacancy, schooldId);
      const newVacancy = await getVacancy(id);
      dispatch(new AddItemAction(newVacancy));
    },
    [createVacancy, dispatch, getVacancy],
  );

  const update = useCallback(
    async (
      id: string,
      schoolId: string,
      update: UpdateVacancyDto,
      lessonUpdate?: {
        substitutes: Teacher[];
        lessons: SubstituteSchedulerLesson[];
      },
    ) => {
      const oldVacancy =
        vacancies.find((v) => v.id === id) || (await getVacancy(id));

      const apply =
        hasPermissions([PERMISSIONS.CanAcceptTeacherOnVacancy]) === false &&
        oldVacancy.absentTeacher.id !== currentTeacher?.id;
      const vacancy = await updateVacancyWithLessons(
        id,
        schoolId,
        oldVacancy.lessons,
        update,
        lessonUpdate
          ? {
              lessons: lessonUpdate?.lessons,
              substitutes: lessonUpdate?.substitutes,
              mode: apply ? "apply" : "assign",
            }
          : undefined,
      );
      dispatch(new UpdateItemAction(id, vacancy));
      return vacancy;
    },
    [
      vacancies,
      getVacancy,
      hasPermissions,
      currentTeacher?.id,
      updateVacancyWithLessons,
      dispatch,
    ],
  );

  return {
    vacancies,
    dispatch,
    getAll,
    add,
    update,
  };
}
